<script setup lang="ts">
import InlineLink from '@/components/InlineLink.vue';
import InlineMessage from '@/components/InlineMessage.vue';
import { useAuth } from '@/composables/useAuth.js';
import { type MessageSchema } from '@/i18n';
import Button from 'primevue/button';
import Divider from 'primevue/divider';
import InputText from 'primevue/inputtext';
import Password from 'primevue/password';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

defineProps<{ token: string }>();

const email = ref('');
const password = ref('');
const passwordConfirmation = ref('');

const { resetPassword, errors, isFetching } = useAuth();
errors.value = {};

const { t } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });

function hasError(fieldName: string) {
  return !!errors.value[fieldName] && errors.value[fieldName].length > 0;
}
</script>

<template>
  <main>
    <form @submit.prevent="resetPassword(email, password, passwordConfirmation, token)">
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-2">
          <label for="email">{{ t('auth.user.form.label.mail') }}</label>
          <div>
            <InputText
              id="email"
              v-model="email"
              type="email"
              class="w-full"
              :placeholder="t('auth.user.form.placeholder.mail')"
              :invalid="hasError('email')"
            />
          </div>
          <InlineMessage v-if="hasError('email')" severity="error">
            {{ errors.email[0] }}
          </InlineMessage>
        </div>
        <div class="flex flex-col gap-2">
          <label for="password">{{ t('auth.user.form.label.password') }}</label>
          <div>
            <Password
              id="password"
              v-model="password"
              :prompt-label="t('auth.user.form.placeholder.password')"
              :weak-label="t('auth.user.form.label.password_weak')"
              :medium-label="t('auth.user.form.label.password_medium')"
              :strong-label="t('auth.user.form.label.password_strong')"
              :invalid="hasError('password')"
              :placeholder="t('auth.user.form.placeholder.password')"
              toggle-mask
              class="w-full"
            />
          </div>
          <InlineMessage v-if="hasError('password')" severity="error">
            {{ errors.password[0] }}
          </InlineMessage>
        </div>

        <div class="flex flex-col gap-2">
          <label for="passwordConfirmation">
            {{ t('auth.user.form.label.password_confirmation') }}
          </label>
          <div>
            <Password
              id="passwordConfirmation"
              v-model="passwordConfirmation"
              :prompt-label="t('auth.user.form.placeholder.password')"
              :weak-label="t('auth.user.form.label.password_weak')"
              :medium-label="t('auth.user.form.label.password_medium')"
              :strong-label="t('auth.user.form.label.password_strong')"
              :invalid="hasError('password')"
              :placeholder="t('auth.user.form.placeholder.password_confirmation')"
              toggle-mask
              class="w-full"
            />
          </div>
          <InlineMessage v-if="hasError('password')" severity="error">
            {{ errors.password[0] }}
          </InlineMessage>
        </div>

        <Button :loading="isFetching" :disabled="isFetching" :label="t('auth.forgotPassword.reset')" class="mt-8 w-full" size="large" type="submit" />
      </div>
    </form>
    <Divider />
    <p>Retour au <InlineLink destination="/login">Login</InlineLink></p>
  </main>
</template>
